import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { slugify } from "@/utils";
import {
  Filters,
  ModalAlert,
  PremiumFloating,
  UserCard,
  UserCardData,
  WaitingApproval,
} from "@/components";
import {
  searchService,
  interestsService,
  chatService,
  FiltersRequest,
} from "@/services";
import { useInterests, useUser } from "@/contexts";
import { DocumentData, QueryDocumentSnapshot } from "firebase/firestore";
import { useNavigate } from "react-router";
import { useCardLock, usePermissions, usePresenceMap } from "@/hooks";
import "./Search.css";

const PAGE_SIZE = 12;
const SEARCH_DEBOUNCE = 500;

const SearchScreen: React.FC = () => {
  const { user: currentUser, userData, excludedUserIds } = useUser();
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const { favoritesGiven } = useInterests();
  const presenceMap = usePresenceMap();

  const favoritesSet = useMemo(() => {
    return new Set(favoritesGiven?.map((f) => f.targetUser));
  }, [favoritesGiven]);

  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const loadingRef = useRef(false);
  const hasMoreRef = useRef(true);
  const cursorRef = useRef<
    QueryDocumentSnapshot<DocumentData> | null | undefined
  >(null);
  const requestIdRef = useRef(0);
  const observerRef = useRef<IntersectionObserver | null>(null);

  const [results, setResults] = useState<UserCardData[]>([]);
  const [filters, setFilters] = useState<FiltersRequest>();

  const [selectedProfileId, setSelectedProfileId] = useState<string | null>(
    null,
  );

  const [favoriteBusyId, setFavoriteBusyId] = useState<string | null>(null);
  const [selectedCardId, setSelectedCardId] = useState<string | null>(null);

  const [hasMore, setHasMore] = useState(true);

  const [status, setStatus] = useState<"idle" | "loading" | "loaded" | "error">(
    "idle",
  );

  const [isAlertOpen, setIsAlertOpen] = useState(false);
  const [alertMessage, setAlertMessage] = useState<string>("");

  const handleRequestChat = async (targetUser: UserCardData) => {
    if (!userData || !currentUser) return;

    const isSubscriber = hasPermission("ACCESS_CHAT_AREA");

    if (!isSubscriber) {
      setAlertMessage("Você precisa ser assinante para enviar mensagens.");
      setIsAlertOpen(true);
      return;
    }

    try {
      const chatId = await chatService.createOrGetConversation(
        currentUser.uid,
        userData,
        targetUser.uid,
        targetUser,
        isSubscriber,
      );

      navigate("/mensagens", { state: { selectedChatId: chatId } });
    } catch (error) {
      console.error("Erro ao iniciar conversa:", error);
    }
  };

  const { isCardLocked } = useCardLock(userData);

  useEffect(() => {
    if (!excludedUserIds?.length) return;

    const excluded = new Set(excludedUserIds);

    setResults((prev) => prev.filter((p) => !excluded.has(p.uid)));
  }, [excludedUserIds]);

  const loadProfiles = useCallback(
    async (reset = false) => {
      if (!userData?.uid || !userData?.gender) return;

      if (loadingRef.current) return;

      if (!reset && !hasMoreRef.current) return;

      loadingRef.current = true;
      setStatus("loading");

      const requestId = ++requestIdRef.current;

      try {
        if (reset) {
          cursorRef.current = null;
          hasMoreRef.current = true;

          setHasMore(true);
          setResults([]);
          setSelectedProfileId(null);
        }

        const { profiles, lastDoc } = await searchService.searchProfiles({
          uid: userData.uid,
          gender: userData.gender,
          pageSize: PAGE_SIZE,
          filters,
          userLocation: userData.location?.geopoint,
          lastDoc: reset ? null : cursorRef.current,
        });

        if (requestId !== requestIdRef.current) return;

        cursorRef.current = lastDoc;

        if (!lastDoc) {
          hasMoreRef.current = false;
          setHasMore(false);
        }

        setResults((prev) => {
          const merged = reset ? profiles : [...prev, ...profiles];
          const uniqueMap = new Map();

          for (const item of merged) {
            uniqueMap.set(item.uid, item);
          }

          return Array.from(uniqueMap.values());
        });

        setStatus("loaded");
      } catch (error) {
        console.error("Erro ao carregar perfis:", error);

        if (requestId === requestIdRef.current) {
          setStatus("error");
        }
      } finally {
        if (requestId === requestIdRef.current) {
          loadingRef.current = false;
        }
      }
    },
    [filters, userData?.uid, userData?.gender, userData?.location?.geopoint],
  );

  const handleFavorite = useCallback((user: UserCardData) => {
    handleToggleFavorite(user);
  }, []);

  const handleToggleFavorite = async (card: UserCardData) => {
    if (!currentUser || !userData || !card.uid) return;

    const isSubscriber = hasPermission("LIKE_PROFILES");

    // if (!isSubscriber) {
    //   setAlertMessage("Ops! Você precisa ser assinante para favoritar perfis.");
    //   setIsAlertOpen(true);
    //   return;
    // }

    if (favoriteBusyId === card.uid) return;

    setFavoriteBusyId(card.uid);

    try {
      await interestsService.toggleFavorite(
        currentUser,
        userData,
        card,
        isSubscriber,
      );
    } catch (err) {
      console.error("Erro ao alternar favorito na busca:", err);
    } finally {
      setFavoriteBusyId(null);
    }
  };

  useEffect(() => {
    if (!userData?.uid) return;

    const timer = setTimeout(() => {
      loadProfiles(true);
    }, SEARCH_DEBOUNCE);

    return () => clearTimeout(timer);
  }, [filters, userData?.uid]);

  useEffect(() => {
    if (!sentinelRef.current) return;

    observerRef.current?.disconnect();

    observerRef.current = new IntersectionObserver((entries) => {
      const first = entries[0];

      if (first?.isIntersecting && !loadingRef.current && hasMoreRef.current) {
        loadProfiles(false);
      }
    });

    observerRef.current.observe(sentinelRef.current);

    return () => {
      observerRef.current?.disconnect();
    };
  }, [loadProfiles, results.length, hasMore]);

  if (userData?.awaitingApproval) {
    return (
      <div className="feed-page container">
        <WaitingApproval username={userData.username || ""} />
      </div>
    );
  }

  return (
    <div className="search-page">
      <div className="search-screen">
        <div className="search-layout container">
          <Filters onFiltersChange={(filters) => setFilters(filters)} />

          <main className="search-results">
            {results.length > 0 ? (
              <>
                <div className="results-grid_search">
                  {results.map((card, index) => (
                    <UserCard
                      key={`${card.uid}-${index}`}
                      data={card}
                      isLocked={isCardLocked(card)}
                      isFavorited={favoritesSet.has(card.uid)}
                      presence={presenceMap[card.uid]}
                      selected={selectedCardId === card.uid}
                      favoriteBusyId={favoriteBusyId}
                      onNavigate={() => {
                        navigate("/perfil/" + slugify(card.username));
                      }}
                      onSelect={(uid) => {
                        setSelectedCardId((prev) =>
                          prev === uid ? null : uid,
                        );
                      }}
                      onStartChat={handleRequestChat}
                      onFavorite={handleFavorite}
                    />
                  ))}
                </div>

                <div
                  ref={sentinelRef}
                  className="results-sentinel"
                  style={{ height: "10px" }}
                />
              </>
            ) : (
              <div className="results-empty-search">
                {status === "loading"
                  ? "Buscando rostos..."
                  : "Use os filtros para encontrar perfis."}
              </div>
            )}

            {status === "loading" && results.length > 0 && (
              <div
                style={{ textAlign: "center", padding: "20px", color: "#666" }}
              >
                Buscando novos rostos...
              </div>
            )}

            {!hasMore && results.length > 0 && (
              <div
                style={{
                  textAlign: "center",
                  color: "#666",
                  marginTop: "20px",
                  paddingBottom: "25px",
                }}
              >
                Você chegou ao fim!
              </div>
            )}
          </main>
        </div>
      </div>

      {!userData?.planInfo?.active && (
        <PremiumFloating navigate={() => navigate("/planos")} />
      )}

      <ModalAlert
        title={alertMessage || "Ops!"}
        isOpen={isAlertOpen}
        onClose={() => setIsAlertOpen(false)}
        onSubscribe={() => {
          navigate("/planos");
          setIsAlertOpen(false);
        }}
      />
    </div>
  );
};

export default SearchScreen;
